// src/hooks/useSubscriptionStatus.js
//
// TWO HOOKS:
//   useSubscriptionStatus()  — fetches if not loaded. Use ONCE in SecurityDashboard.
//   useSubscriptionData()    — read-only, no fetch. Use everywhere else (sidebar, banners, gates).

import { useEffect } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import * as actions from "../app/modules/Dashbord/_redux/dashboard/Actions";

// ── Shared derivation ─────────────────────────────────────────────────────────

const deriveStatus = (subscription) => {
  const sub    = subscription?.subscription || subscription;
  const plan   = subscription?.plan;
  const status = sub?.status;

  const isLoaded    = subscription !== null && subscription !== undefined;
  const isActive    = status === "active";
  const isTrial     = status === "trial";
  const isPastDue   = status === "past_due";
  const isSuspended = status === "suspended";
  const isCancelled = status === "cancelled";
  const hasAccess   = !isLoaded ? true : (isActive || isTrial);
  const needsPayment = !isLoaded ? false : (isPastDue || isSuspended || isCancelled);

  const trialDaysLeft = (() => {
    if (!isTrial || !sub?.trial_ends_at) return null;
    const diff = new Date(sub.trial_ends_at) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  })();

  const canUseFeature = (featureKey) => {
    if (!isLoaded) return true;
    if (!hasAccess) return false;
    if (!plan?.features) return true;
    return plan.features[featureKey] === true;
  };

  return {
    sub, plan, status,
    isActive, isTrial, isPastDue, isSuspended, isCancelled,
    hasAccess, needsPayment, trialDaysLeft, canUseFeature, isLoaded,
  };
};

// ── READ + FETCH — use only in SecurityDashboard (once per page tree) ─────────

export function useSubscriptionStatus() {
  const dispatch = useDispatch();

  const { org, subscription, subscriptionLoading } = useSelector(
    (state) => ({
      org:                 state.auth.organization,
      subscription:        state.dashboard.subscription,
      subscriptionLoading: state.dashboard.subscriptionLoading,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (!org) return;
    if (subscriptionLoading) return;
    if (subscription !== null && subscription !== undefined) return;
    dispatch(actions.fetchAllData("subscription", org.organization_id, org.type));
  }, [org]);

  return deriveStatus(subscription);
}

// ── READ ONLY — use in sidebar, SubscriptionBanner, FeatureGate, anywhere else ─

export function useSubscriptionData() {
  const subscription = useSelector(
    (state) => state.dashboard.subscription,
    shallowEqual
  );
  return deriveStatus(subscription);
}